import React, { useEffect, useState, useContext } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Accordion,
  Flex,
  Box,
  Button,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import AssignAccordionItem from './AssignAccordionItem';
import { getAssignEmployee } from './AssignSelector';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import type { Employee } from '../../contexts/types';

/**
 * カテゴリ設定
 */
export interface CategoryConfig {
  /** カテゴリ識別子 */
  id: string;
  /** 表示名 */
  label: string;
  /** 最大割当人数（0 = 無制限） */
  maxAssignees: number;
  /** 組織分類ID（子孫）でフィルタ ※正式なIDは要確認 */
  descendantOrganizationClassIds?: string[];
}

/**
 * カテゴリごとの割当済み従業員情報
 */
export interface CategoryAssignee {
  categoryId: string;
  categoryName: string;
  employees: CategoryEmployee[];
}

/**
 * 割当済み従業員
 */
export interface CategoryEmployee {
  employeeId: string;
  employeeName: string;
  divisionId?: string;
  divisionName?: string;
  positionName?: string;
}

interface AssignByCategoryModalProps {
  /** モーダルの開閉状態 */
  isOpen: boolean;
  /** 閉じるコールバック */
  onClose: () => void;
  /** 確定コールバック */
  onConfirm: (assignees: CategoryAssignee[]) => void;
  /** モーダルタイトル */
  title: string;
  /** 表示するカテゴリ設定 */
  categories: CategoryConfig[];
  /** 初期割当状態 */
  initAssignees: CategoryAssignee[];
}

/** カテゴリ別の選択済み従業員を管理する型 */
interface CategorySelection {
  [categoryId: string]: Employee[];
}

/**
 * カテゴリ別担当者選択モーダル（共通コンポーネント）
 *
 * クロスチェック担当者、資材担当役員、承認者の選択に共通で使用。
 * categories propsでカテゴリ設定を渡すことで、各用途に対応する。
 */
const AssignByCategoryModal: React.FC<AssignByCategoryModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  categories,
  initAssignees,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);

  /** 従業員マスタ情報 */
  const [employees, setEmployees] = useState<Employee[]>([]);

  /** カテゴリ別の選択済み従業員 */
  const [selections, setSelections] = useState<CategorySelection>({});

  /**
   * 初回マウント時に従業員情報を取得
   */
  useEffect(() => {
    fetchData();
  }, []);

  /**
   * モーダルが開かれた時に初期選択状態を復元
   */
  useEffect(() => {
    if (isOpen) {
      const categoryIds = categories.map(c => c.id);
      const initialSelections: CategorySelection = {};
      categoryIds.forEach(catId => {
        const existing = initAssignees.find(a => a.categoryId === catId);
        if (existing && existing.employees.length > 0) {
          const restored = existing.employees
            .map(e => employees.find(emp => emp.employeeId === e.employeeId))
            .filter((emp): emp is Employee => emp !== undefined);
          initialSelections[catId] = restored;
        } else {
          initialSelections[catId] = [];
        }
      });
      setSelections(initialSelections);
    }
  }, [isOpen, initAssignees, categories, employees]);

  /**
   * 従業員マスタ情報を取得する
   */
  const fetchData = async () => {
    const result = await getAssignEmployee(WebAPI.request);
    if (result) {
      setEmployees(result);
    } else {
      toast({
        title: t('common.message.initialDisplayError'),
        description: t('common.message.fetchEmployeesFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /**
   * カテゴリごとに選択した従業員を更新する
   */
  const changeSelectedEmployees = (categoryId: string, selectedEmployees: Employee[]) => {
    setSelections(prev => ({
      ...prev,
      [categoryId]: selectedEmployees,
    }));
  };

  /**
   * 全カテゴリで1名以上選択されているか判定
   */
  const isDisabledOk = () => {
    const categoryIds = categories.map(c => c.id);
    return !categoryIds.every(catId => (selections[catId] ?? []).length > 0);
  };

  /**
   * 確定ボタン押下時
   */
  const handleOk = () => {
    const assignees: CategoryAssignee[] = categories.map(cat => ({
      categoryId: cat.id,
      categoryName: cat.label,
      employees: (selections[cat.id] ?? []).map(emp => ({
        employeeId: emp.employeeId ?? "",
        employeeName: emp.employeeName ?? "",
        divisionId: emp.divisionId ?? "",
        divisionName: emp.divisionName ?? "",
        positionName: emp.positionName ?? "",
      })),
    }));

    onConfirm(assignees);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} isCentered scrollBehavior="inside" size="2xl">
      <ModalOverlay />
      <ModalContent maxHeight="90vh">
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {categories.length === 0 ? (
            <Box textAlign="center" py={10} color="gray.500">
              {t('features.Approve.assignment.message.noCategorySelected')}
            </Box>
          ) : (
            <>
              <Accordion allowMultiple>
                {categories.map(cat => {
                  const initSelectedEmployeeIds = (selections[cat.id] ?? [])
                    .map(emp => emp.employeeId)
                    .filter((id): id is string => id !== undefined);

                  return (
                    <AssignAccordionItem
                      key={cat.id}
                      title={cat.label}
                      id={cat.id}
                      changeSelectedEmployees={changeSelectedEmployees}
                      conditionDescendantOrganizationClassIds={cat.descendantOrganizationClassIds}
                      employees={employees}
                      initSelectedEmployeeIds={initSelectedEmployeeIds}
                      disabledEmployeeIds={[]}
                      maxAssignees={cat.maxAssignees}
                    />
                  );
                })}
              </Accordion>

              <Flex alignItems="center" justifyContent="right" marginTop={2}>
                <Button colorScheme="blue" isDisabled={isDisabledOk()} onClick={handleOk}>{t('common.button.confirmAction')}</Button>
              </Flex>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AssignByCategoryModal;
